import classNames from "classnames";
import React, {
  TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
} from "react";
import { __ } from "@common/helpers/i18n";
import { icon as appIcon } from "@common/helpers/icons";
import { TextAreaProps } from "@common/types";
import { TextareaPrimitive } from "@app/components/ui/textarea";
import { CounterBadge } from "../text";
import { EmojiPicker, EmojiPickerProps } from "./EmojiPicker";
import { Input } from "./Input";
import { getResolvedFieldPlaceholder } from "./fieldPlaceholder";

function getValueLength(value: unknown) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).length;
  }

  return 0;
}

export const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & TextAreaProps
>(function TextArea(
  {
    label,
    error,
    hint,
    loading,
    className,
    icon,
    iconPosition,
    emojiPicker,
    emojiPickerPlacement,
    charLeft,
    autoHeight,
    emojiIcon,
    onChange,
    onInputChange,
    resizeable,
    onAutoHeight,
    success,
    autoWidth,
    maxLength,
    footer,
    onSelect,
    onClick,
    onKeyUp,
    placeholder,
    ...props
  },
  ref,
) {
  const [length, setLength] = React.useState(() =>
    getValueLength(props.value ?? props.defaultValue),
  );
  const [emojiPickerVisible, setEmojiPickerVisible] = React.useState(false);
  const innerRef = React.useRef<HTMLTextAreaElement>(null);
  const autoHeightRef = React.useRef<number | null>(null);
  const selectionRef = React.useRef({ start: 0, end: 0 });
  const currentLength =
    props.value !== undefined ? getValueLength(props.value) : length;
  const remaining =
    typeof charLeft !== "undefined"
      ? charLeft
      : typeof maxLength === "number"
        ? maxLength - currentLength
        : undefined;
  const shouldShowCounter =
    typeof remaining !== "undefined" && currentLength > 0;
  const hasVisibleIcon = Boolean(icon && icon !== true);
  const resolvedIconPosition = iconPosition ?? "start";
  const hasStartIcon = hasVisibleIcon && resolvedIconPosition !== "end";
  const hasEndIcon =
    (hasVisibleIcon && resolvedIconPosition === "end") || emojiPicker;
  const resolvedPlaceholder = getResolvedFieldPlaceholder(
    label,
    placeholder,
    "textarea",
  );
  const normalizeValue = React.useCallback(
    (value: string) =>
      typeof maxLength === "number" ? value.slice(0, maxLength) : value,
    [maxLength],
  );

  useImperativeHandle(ref, () => innerRef.current!);

  useEffect(() => {
    function closeEmoji() {
      setEmojiPickerVisible(false);
    }
    document.addEventListener("click", closeEmoji);
    return () => document.removeEventListener("click", closeEmoji);
  }, []);

  const resizeToContent = React.useCallback(() => {
    const textarea = innerRef.current;
    if (!textarea || !autoHeight) return;

    textarea.style.height = "auto";

    const nextHeight = Math.ceil(textarea.scrollHeight);
    const previousHeight = autoHeightRef.current;

    textarea.style.height = `${nextHeight}px`;

    if (previousHeight === null || Math.abs(previousHeight - nextHeight) > 1) {
      autoHeightRef.current = nextHeight;
      onAutoHeight?.(nextHeight);
    }
  }, [autoHeight, onAutoHeight]);

  useLayoutEffect(() => {
    const textarea = innerRef.current;
    if (!textarea || !autoHeight) return;

    resizeToContent();
    textarea.addEventListener("input", resizeToContent);
    return () => textarea.removeEventListener("input", resizeToContent);
  }, [props.value, autoHeight, resizeToContent]);

  const saveSelection = React.useCallback(() => {
    const el = innerRef.current;
    if (!el) return;

    selectionRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd,
    };
  }, []);

  const onEmojiSelect: EmojiPickerProps["onEmojiInsert"] = (emoji) => {
    setEmojiPickerVisible(false);
    if (!innerRef.current) {
      return;
    }
    const el = innerRef.current;
    const currentValue =
      props.value !== undefined ? String(props.value) : el.value;
    const selectionStart = Math.min(
      selectionRef.current.start,
      currentValue.length,
    );
    const selectionEnd = Math.min(
      selectionRef.current.end,
      currentValue.length,
    );
    const textBeforeCursorPosition = currentValue.substring(0, selectionStart);
    const textAfterCursorPosition = currentValue.substring(
      selectionEnd,
      currentValue.length,
    );
    const nextValue = normalizeValue(
      textBeforeCursorPosition + emoji.native + textAfterCursorPosition,
    );
    onInputChange?.(nextValue);
    setLength(nextValue.length);
    window.requestAnimationFrame(() => {
      const nextCursorPosition = Math.min(
        selectionStart + emoji.native.length,
        nextValue.length,
      );
      el.focus();
      el.setSelectionRange(nextCursorPosition, nextCursorPosition);
      selectionRef.current = {
        start: nextCursorPosition,
        end: nextCursorPosition,
      };
    });
    //this.props.onEmojiSelect?.(emoji);
  };

  return (
    <Input
      label={label}
      error={error}
      hint={hint}
      loading={loading}
      iconPosition={iconPosition}
      success={success}
      footer={
        shouldShowCounter || footer ? (
          <>
            {shouldShowCounter && (
              <CounterBadge
                value={__("%{count} characters remaining", {
                  count: remaining,
                })}
                tone={remaining < 0 ? "danger" : "neutral"}
                size="sm"
                className="mt-1.5 flex w-fit"
              />
            )}
            {footer}
          </>
        ) : undefined
      }
    >
      <div className="w-full">
        <div className="relative">
          <TextareaPrimitive
            variant={
              error
                ? "danger"
                : success
                  ? "success"
                  : props.disabled
                    ? "disabled"
                    : "default"
            }
            className={classNames(
              autoWidth ? "w-auto" : "block w-full",
              className,
              !resizeable ? "resize-none" : "",
              autoHeight && "max-h-48 transition-colors",
              "input-padding",
              hasStartIcon && "ps-11",
              hasEndIcon && "pe-11",
            )}
            ref={innerRef}
            maxLength={maxLength}
            placeholder={resolvedPlaceholder}
            onSelect={(e) => {
              onSelect?.(e);
              saveSelection();
            }}
            onClick={(e) => {
              onClick?.(e);
              saveSelection();
            }}
            onKeyUp={(e) => {
              onKeyUp?.(e);
              saveSelection();
            }}
            onChange={(e) => {
              const nextValue = normalizeValue(e.target.value);
              e.target.value = nextValue;
              onChange?.(e);
              onInputChange?.(nextValue);
              setLength(nextValue.length);
              saveSelection();
            }}
            {...props}
          />
          {emojiPicker && (
            <EmojiPicker
              isOpen={emojiPickerVisible}
              placement={emojiPickerPlacement}
              onEmojiInsert={onEmojiSelect}
              renderTarget={(ref: any) => (
                <button
                  type="button"
                  ref={ref}
                  className="pointer-events-auto absolute end-3 top-5 hidden h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/20 md:flex"
                  aria-label={__("Add emoji")}
                  aria-expanded={emojiPickerVisible}
                  onClick={(e) => {
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    e.stopPropagation();
                    setEmojiPickerVisible(!emojiPickerVisible);
                  }}
                >
                  {emojiIcon || appIcon("mood-smile", { className: "h-4 w-4" })}
                </button>
              )}
            />
          )}
          {icon &&
            icon !== true &&
            (iconPosition !== "none" ? (
              <div
                className={classNames(
                  "pointer-events-none absolute top-0 flex h-11 w-11 items-center justify-center text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:stroke-[2]",
                  resolvedIconPosition === "end" ? "end-0" : "start-0",
                )}
              >
                {icon}
              </div>
            ) : (
              icon
            ))}
        </div>
      </div>
    </Input>
  );
});
